import type { AnyBlockHandle, Unsubscribe } from './types';

type RegisterCallback = (handle: AnyBlockHandle) => void;

export class HandleRegistry {
  private handles = new Map<string, AnyBlockHandle>();
  private registerCallbacks = new Map<string, RegisterCallback[]>();
  private disposed = false;

  register(semanticId: string, handle: AnyBlockHandle): void {
    if (this.disposed) return;

    if (this.handles.has(semanticId)) {
      if (import.meta.env.DEV) {
        throw new Error(
          `[block-events] Duplicate semanticId "${semanticId}" registered. Each block must have a unique semanticId.`,
        );
      } else {
        console.error(
          `[block-events] Duplicate semanticId "${semanticId}". Second registration wins.`,
        );
      }
    }

    this.handles.set(semanticId, handle);

    const callbacks = this.registerCallbacks.get(semanticId);
    if (callbacks) {
      for (const cb of callbacks) {
        try {
          cb(handle);
        } catch (err) {
          console.error('[block-events] onRegister callback error:', err);
        }
      }
      this.registerCallbacks.delete(semanticId);
    }
  }

  unregister(semanticId: string): void {
    const handle = this.handles.get(semanticId);
    if (handle) {
      handle.dispose();
      this.handles.delete(semanticId);
    }
  }

  get(semanticId: string): AnyBlockHandle | undefined {
    return this.handles.get(semanticId);
  }

  has(semanticId: string): boolean {
    return this.handles.has(semanticId);
  }

  onRegister(semanticId: string, callback: RegisterCallback): Unsubscribe {
    if (this.handles.has(semanticId)) {
      callback(this.handles.get(semanticId)!);
      return () => {};
    }

    const existing = this.registerCallbacks.get(semanticId) ?? [];
    existing.push(callback);
    this.registerCallbacks.set(semanticId, existing);

    return () => {
      const list = this.registerCallbacks.get(semanticId);
      if (list) {
        const idx = list.indexOf(callback);
        if (idx !== -1) list.splice(idx, 1);
        if (list.length === 0) this.registerCallbacks.delete(semanticId);
      }
    };
  }

  entries(): IterableIterator<[string, AnyBlockHandle]> {
    return this.handles.entries();
  }

  dispose(): void {
    this.disposed = true;
    for (const [, handle] of this.handles) {
      handle.dispose();
    }
    this.handles.clear();
    this.registerCallbacks.clear();
  }
}
